import { useRef, useEffect, useState } from 'react'
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  ListBulletIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline'
import { ChallengeService } from '@duck/api-definitions'
import { cn } from '@duck/ui'
import { useDebouncedEventListener, useEventListener, useEventEmitter } from '@modules/EventBusModule'
import { useTar } from '@/modules/i18n'
import DOMPurify from 'dompurify'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ToggleGroup,
  ToggleGroupItem,
} from '@duck/ui'
import { useIsMobile } from '@duck/ui'
import {
  cleanChartContainersFromElement,
  cleanChartContainersFromHTML,
  getContentChartIds,
  saveSelection,
  insertNodeAtSelection,
  initializeChart,
  createTrashButton,
  rebindExistingTrashButtons,
  ensureChartTrashButtons,
  rangeToCharOffset,
  charOffsetToRange,
} from './wysiwygUtils'

interface WysiwygEditorProps {
  onChange?: (content: string) => void
  contentGetter?: () => string
  content?: string
  placeholder?: string
  className?: string
  readOnly?: boolean
  /** When true, this editor reacts to solution:insertHtml and chart:added (e.g. Solution tab only). */
  acceptSolutionInsert?: boolean
  handleChartAdded?: (content: string, chart: ChallengeService.Chart) => void
  handleChartRemoved?: (content: string, remainingChartIds: string[]) => void
  startingCharts?: ChallengeService.WorkState['charts']
  editorClassName?: string
}

export const WysiwygEditor = ({
  onChange,
  contentGetter,
  content = '',
  placeholder = 'Start typing...',
  className = '',
  readOnly = false,
  acceptSolutionInsert = false,
  startingCharts = {},
  handleChartAdded,
  handleChartRemoved,
  editorClassName,
}: WysiwygEditorProps) => {
  const { t, isRtl } = useTar()
  const isMobile = useIsMobile()
  const editorRef = useRef<HTMLDivElement>(null)
  const loadedChartIds = useRef<string[]>(Object.keys(startingCharts))
  const [formatBlock, setFormatBlock] = useState<string>('p')
  const [textFormatGroupValue, setTextFormatGroupValue] = useState<string>('')
  const [listGroupValue, setListGroupValue] = useState<string>('')
  const [directionGroupValue, setDirectionGroupValue] = useState<string>('')
  const savedSelectionRef = useRef<Range | null>(null)
  const eventListenerCleanupRef = useRef<(() => void) | void>(undefined)
  const lastSerializedContentRef = useRef<string | null>(null)
  const skipNextContentSyncRef = useRef<boolean>(false)
  const emitChartsTriggerRedraw = useEventEmitter('charts:triggerRedraw')
  const editorIdRef = useRef<string>('')
  if (!editorIdRef.current) {
    editorIdRef.current = `wysiwyg-${Math.random().toString(36).slice(2, 10)}`
  }

  // Save selection on user interaction
  useEffect(() => {
    if (readOnly || !editorRef.current) {
      return
    }

    const saveSelectionHandler = () => {
      savedSelectionRef.current = saveSelection(editorRef)
    }

    const editor = editorRef.current
    editor.addEventListener('click', saveSelectionHandler)
    editor.addEventListener('keyup', saveSelectionHandler)
    editor.addEventListener('mouseup', saveSelectionHandler)

    return () => {
      editor.removeEventListener('click', saveSelectionHandler)
      editor.removeEventListener('keyup', saveSelectionHandler)
      editor.removeEventListener('mouseup', saveSelectionHandler)
    }
  }, [readOnly])

  // Detect if text contains any Hebrew characters
  const containsHebrew = (text: string): boolean => {
    if (text.length === 0) return false
    // Check if any character is Hebrew (Unicode range \u0590-\u05FF)
    return /[\u0590-\u05FF]/.test(text)
  }

  // Get text content from an element, ignoring nested formatting
  const getTextContent = (element: Node): string => {
    if (element.nodeType === Node.TEXT_NODE) {
      return element.textContent || ''
    }
    if (element.nodeType === Node.ELEMENT_NODE) {
      const el = element as HTMLElement
      // Skip chart containers and other non-text elements
      if (el.classList.contains('chart-container')) {
        return ''
      }
      let text = ''
      for (const child of Array.from(el.childNodes)) {
        text += getTextContent(child)
      }
      return text
    }
    return ''
  }

  // Wrap bare text nodes that are direct children of the editor root
  // in a block-level element so they are processed by RTL/LTR logic.
  const wrapBareTextNodesInRoot = () => {
    const root = editorRef.current
    if (!root) return

    const childNodes = Array.from(root.childNodes)
    childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? ''
        // Ignore pure whitespace text nodes to avoid creating empty blocks
        if (text.trim().length === 0) {
          return
        }

        const wrapper = document.createElement('p')
        wrapper.textContent = text
        root.replaceChild(wrapper, node)
      }
    })
  }

  // Apply RTL/LTR direction to block elements based on Hebrew detection
  const applyRtlToBlocks = () => {
    if (!editorRef.current) return

    // Ensure bare text nodes at the root are wrapped so they can be styled
    wrapBareTextNodesInRoot()

    const blockElements = editorRef.current.querySelectorAll(
      'p, div, h1, h2, h3, h4, h5, h6, li, blockquote, pre'
    )

    blockElements.forEach((element) => {
      const el = element as HTMLElement
      // Skip chart containers
      if (el.classList.contains('chart-container')) {
        return
      }

      const override = el.dataset.dirMode
      if (override === 'rtl' || override === 'ltr') {
        el.setAttribute('dir', override)
        return
      }

      const textContent = getTextContent(el)
      const blockIsRtl = containsHebrew(textContent)
      if (blockIsRtl) {
        el.setAttribute('dir', 'rtl')
      } else {
        el.setAttribute('dir', 'ltr')
      }
    })
  }

  const setTextDirection = (direction: 'rtl' | 'ltr') => {
    if (!editorRef.current) return

    const selection = window.getSelection()
    const root = editorRef.current

    if (!selection || selection.rangeCount === 0) {
      root.setAttribute('dir', direction)
      root.dataset.dirMode = direction
      updateContent()
      return
    }

    let container: Node | null = selection.getRangeAt(0).commonAncestorContainer

    if (container.nodeType === Node.TEXT_NODE) {
      container = container.parentNode
    }

    let block: HTMLElement | null = null

    while (container && container !== root) {
      if (
        container instanceof HTMLElement &&
        /^(P|DIV|H1|H2|H3|H4|H5|H6|LI|BLOCKQUOTE|PRE)$/i.test(container.tagName)
      ) {
        block = container
        break
      }
      container = container.parentNode
    }

    if (!block) {
      root.setAttribute('dir', direction)
      root.dataset.dirMode = direction
      updateContent()
      return
    }

    block.setAttribute('dir', direction)
    block.dataset.dirMode = direction
    updateContent()
  }

  const externalContent = contentGetter?.() ?? content ?? ''

  // Content synchronization effect
  useEffect(() => {
    if (!editorRef.current) {
      return
    }

    const currentContent = editorRef.current.innerHTML
    const newContent = externalContent
    const normalizedCurrentContent = cleanChartContainersFromHTML(currentContent)

    // If this content value is just the echo of our own latest
    // `updateContent` call, avoid resetting innerHTML – this would
    // clear chart internals and reset the caret for no visual change.
    if (
      skipNextContentSyncRef.current &&
      lastSerializedContentRef.current !== null &&
      lastSerializedContentRef.current === newContent
    ) {
      skipNextContentSyncRef.current = false
      return
    }

    // Only update if content actually changed in a meaningful way.
    // If the only difference is chart internals (which we deliberately strip
    // from serialized HTML), skip resetting innerHTML to preserve charts and caret.
    if (currentContent === newContent || normalizedCurrentContent === newContent) {
      return
    }

    const root = editorRef.current
    const sanitized = DOMPurify.sanitize(newContent)

    // Build a new DOM tree for the updated content, but preserve any existing
    // chart container internals by copying them over from the current DOM.
    const temp = document.createElement('div')
    temp.innerHTML = sanitized

    const existingCharts = root.querySelectorAll<HTMLElement>('.chart-container[data-chart-id]')
    existingCharts.forEach((existingChart) => {
      const id = existingChart.getAttribute('data-chart-id')
      if (!id) return
      const tempChart = temp.querySelector<HTMLElement>(`.chart-container[data-chart-id="${id}"]`)
      if (tempChart) {
        tempChart.innerHTML = existingChart.innerHTML
      }
    })

    // Preserve saved selection across innerHTML replacement by converting to char offset
    const savedCharOffset = savedSelectionRef.current
      ? rangeToCharOffset(root, savedSelectionRef.current)
      : null

    root.innerHTML = temp.innerHTML

    // Restore saved selection from char offset so chart/query insertion targets the right position
    if (savedCharOffset !== null) {
      savedSelectionRef.current = charOffsetToRange(root, savedCharOffset)
    }

    // After external content updates, re-apply RTL logic, rebind trash buttons,
    // and restore any missing chart trash buttons.
    setTimeout(() => {
      applyRtlToBlocks()
      rebindExistingTrashButtons(updateContent)
      if (!readOnly && editorRef.current) {
        ensureChartTrashButtons(editorRef.current, updateContent)
      }
    }, 0)
  }, [externalContent])

  const rerenderCharts = (): (() => void) | void => {
    if (!editorRef.current || Object.keys(startingCharts).length === 0) {
      return
    }
    const cleanupFunctions: (() => void)[] = []
    cleanChartContainersFromElement(editorRef.current)
    if (Object.keys(startingCharts).length > 0) {
      Object.entries(startingCharts).forEach(([id, chart]) => {
        const cleanup = initializeChart(id, chart, editorRef, readOnly, updateContent)
        if (cleanup) {
          cleanupFunctions.push(cleanup)
        }
      })
    }
    // Apply RTL detection after charts are rendered, and ensure trash buttons exist
    setTimeout(() => {
      applyRtlToBlocks()
      if (!readOnly && editorRef.current) {
        ensureChartTrashButtons(editorRef.current, updateContent)
      }
    }, 0)
    return () => {
      cleanupFunctions.forEach(cleanup => cleanup?.())
    }
  }

  // Chart initialization on mount
  useEffect(() => {
    return rerenderCharts()
  }, [startingCharts])

  // Cleanup event listener cleanups on unmount or when dependencies change
  useEffect(() => {
    return () => {
      if (eventListenerCleanupRef.current) {
        eventListenerCleanupRef.current()
        eventListenerCleanupRef.current = undefined
      }
    }
  }, [startingCharts, readOnly, handleChartAdded])

  useDebouncedEventListener(60, 'charts:triggerRedraw', (event) => {
    // If this redraw was triggered by this exact editor instance,
    // skip it so we don't disrupt the active typing experience.
    if (event.originId && event.originId === editorIdRef.current) {
      return
    }
    // Cleanup previous cleanup if it exists
    if (eventListenerCleanupRef.current) {
      eventListenerCleanupRef.current()
    }
    // Store new cleanup function
    eventListenerCleanupRef.current = rerenderCharts()
  }, [startingCharts])

  useEventListener('chart:added', ({ chart }) => {
    if (!acceptSolutionInsert || !chart.id || !editorRef.current) {
      return
    }

    const chartContainer = document.createElement('div')
    chartContainer.className = `chart-container min-h-10 w-full relative my-2 p-2 border border-border rounded-md overflow-hidden`
    chartContainer.setAttribute('data-chart-id', chart.id)
    const chartContainerId = chartContainer.id || `chart-${chart.id}`
    chartContainer.id = chartContainerId

    // Insert at the saved caret position (works even when editor tab is inactive/invisible)
    insertNodeAtSelection(chartContainer, editorRef, savedSelectionRef.current)

    // Insert Separator
    const separator = document.createElement('div')
    separator.innerHTML = '&nbsp;'
    const afterChart = document.createRange()
    afterChart.setStartAfter(chartContainer)
    afterChart.collapse(true)
    insertNodeAtSelection(separator, editorRef, afterChart)

    // Advance the saved caret past the insert so consecutive adds keep document
    // order instead of each landing above the previous one
    const afterSeparator = document.createRange()
    afterSeparator.setStartAfter(separator)
    afterSeparator.collapse(true)
    savedSelectionRef.current = afterSeparator

    loadedChartIds.current.push(chart.id)
    updateContent()
    if (!handleChartAdded) {
      // Cleanup previous cleanup if it exists
      if (eventListenerCleanupRef.current) {
        eventListenerCleanupRef.current()
      }
      // Store new cleanup function
      eventListenerCleanupRef.current = rerenderCharts()
    }
  }, [acceptSolutionInsert, readOnly, handleChartAdded])

  useEventListener('solution:insertHtml', ({ value: html, deleteButton }) => {
    if (!acceptSolutionInsert || !editorRef.current || readOnly) return
    const temp = document.createElement('div')
    temp.innerHTML = DOMPurify.sanitize(html)
    const baseNode = temp.childNodes.length === 1 && temp.firstChild ? temp.firstChild : temp

    const nodeToInsert =
      deleteButton && !readOnly
        ? (() => {
          const container = document.createElement('div')
          container.className = 'relative my-2'
          const blockId = `solution-block-${Math.random().toString(36).slice(2, 10)}`
          container.id = blockId
          const trashButton = createTrashButton(blockId, updateContent)
          container.appendChild(trashButton)
          container.appendChild(baseNode)
          return container
        })()
        : baseNode

    insertNodeAtSelection(nodeToInsert, editorRef, savedSelectionRef.current)

    // Advance the saved caret past the insert so consecutive adds keep document
    // order instead of each landing above the previous one
    const afterInsert = document.createRange()
    afterInsert.setStartAfter(nodeToInsert)
    afterInsert.collapse(true)
    savedSelectionRef.current = afterInsert

    updateContent()
  }, [acceptSolutionInsert, readOnly])

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value)
    editorRef.current?.focus()
    updateContent()
    // For Solution editors only: emit chart redraw so other Solution editors
    // re-initialize after formatting (bold/italic/lists). Notes must not emit.
    if (acceptSolutionInsert) {
      emitChartsTriggerRedraw({ originId: editorIdRef.current })
    }
  }

  const updateContent = () => {
    if (editorRef.current) {
      const remainingChartIds = getContentChartIds(editorRef.current)
      if (remainingChartIds.length < loadedChartIds.current.length) {
        if (handleChartRemoved) {
          handleChartRemoved(cleanChartContainersFromHTML(editorRef.current.innerHTML), remainingChartIds)
        }
        loadedChartIds.current = remainingChartIds
        return
      }
      const serialized = cleanChartContainersFromHTML(editorRef.current.innerHTML)
      lastSerializedContentRef.current = serialized
      skipNextContentSyncRef.current = true
      onChange?.(serialized)
      // When content changes in a Solution editor (one with charts), notify
      // other Solution editors to redraw their charts. Do not emit from Notes
      // or other non-solution editors so they don't trigger Solution chart redraws.
      if (acceptSolutionInsert) {
        emitChartsTriggerRedraw({ originId: editorIdRef.current })
      }
    }
  }

  const handleInput = () => {
    applyRtlToBlocks()
    updateContent()
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, text)
  }

  return (
    <div
      className={cn('p-2 w-full flex flex-col flex-1 min-h-0 overflow-hidden', className)}
    >
      {/* Toolbar */}
      {!readOnly && (
        <div className="bg-card flex flex-col gap-1 p-2 w-full border-border border-b">
          <span className="text-xs font-medium text-muted-foreground px-1">
            {t('common.formatting')}
          </span>
          <div className="flex items-center gap-1 h-7">
            {/* Headers */}
            <Select
              value={formatBlock}
              onValueChange={(value) => {
                setFormatBlock(value)
                if (value === 'p') {
                  execCommand('formatBlock', '<p>')
                } else {
                  execCommand('formatBlock', `<${value}>`)
                }
              }}
            >
              <SelectTrigger className="w-[140px] h-7 bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="p">{t('common.paragraph')}</SelectItem>
                <SelectItem value="h1">{t('common.heading1')}</SelectItem>
                <SelectItem value="h2">{t('common.heading2')}</SelectItem>
                <SelectItem value="h3">{t('common.heading3')}</SelectItem>
                <SelectItem value="h4">{t('common.heading4')}</SelectItem>
              </SelectContent>
            </Select>

            <div className="w-px h-6 bg-border mx-1" />

            {!isMobile && (
              <>
                {/* Text formatting group */}
                <ToggleGroup
                  type="single"
                  variant="outline"
                  size="sm"
                  value={textFormatGroupValue}
                  onValueChange={(value) => {
                    if (!value) return
                    setTextFormatGroupValue('')
                    const handlers: Record<string, () => void> = {
                      bold: () => execCommand('bold'),
                      italic: () => execCommand('italic'),
                      underline: () => execCommand('underline'),
                    }
                    handlers[value]?.()
                  }}
                  aria-label={t('common.textFormatting') ?? 'Text formatting'}
                >
                  <ToggleGroupItem
                    value="bold"
                    aria-label={t('common.bold')}
                    className="gap-1.5"
                  >
                    <BoldIcon className="w-4 h-4" />
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="italic"
                    aria-label={t('common.italic')}
                    className="gap-1.5"
                  >
                    <ItalicIcon className="w-4 h-4" />
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="underline"
                    aria-label={t('common.underline')}
                    className="gap-1.5"
                  >
                    <UnderlineIcon className="w-4 h-4" />
                  </ToggleGroupItem>
                </ToggleGroup>

                <div className="w-px h-6 bg-border mx-1" />
              </>
            )}

            {/* Lists group */}
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={listGroupValue}
              onValueChange={(value) => {
                if (!value) return
                setListGroupValue('')
                const handlers: Record<string, () => void> = {
                  unordered: () => execCommand('insertUnorderedList'),
                  ordered: () => execCommand('insertOrderedList'),
                }
                handlers[value]?.()
              }}
              aria-label={t('common.lists') ?? 'Lists'}
            >
              <ToggleGroupItem
                value="unordered"
                aria-label={t('common.unorderedList')}
                className="gap-1.5"
              >
                <ListBulletIcon className="w-4 h-4" />
              </ToggleGroupItem>
              <ToggleGroupItem
                value="ordered"
                aria-label={t('common.orderedList')}
                className="gap-1.5"
              >
                <Bars3Icon className="w-4 h-4" />
              </ToggleGroupItem>
            </ToggleGroup>

            <div className="w-px h-6 bg-border mx-1" />

            {/* Text direction group */}
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={directionGroupValue}
              onValueChange={(value) => {
                if (!value) return
                setDirectionGroupValue('')
                const handlers: Record<string, () => void> = {
                  ltr: () => setTextDirection('ltr'),
                  rtl: () => setTextDirection('rtl'),
                }
                handlers[value]?.()
              }}
              aria-label={t('common.formatting')}
            >
              {isRtl ? (
                <>
                  <ToggleGroupItem
                    value="rtl"
                    aria-label={t('common.paragraph')}
                    className="gap-1.5"
                  >
                    <svg
                      className="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 6H20"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 12H20"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 18H20"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5 14L2 12L5 10"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2 12H8"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="ltr"
                    aria-label={t('common.paragraph')}
                    className="gap-1.5"
                  >
                    <svg
                      className="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 6H18"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4 12H18"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4 18H18"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M19 14L22 12L19 10"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M16 12H22"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ToggleGroupItem>
                </>
              ) : (
                <>
                  <ToggleGroupItem
                    value="ltr"
                    aria-label={t('common.paragraph')}
                    className="gap-1.5"
                  >
                    <svg
                      className="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 6H18"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4 12H18"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4 18H18"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M19 14L22 12L19 10"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M16 12H22"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="rtl"
                    aria-label={t('common.paragraph')}
                    className="gap-1.5"
                  >
                    <svg
                      className="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 6H20"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 12H20"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 18H20"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5 14L2 12L5 10"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2 12H8"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ToggleGroupItem>
                </>
              )}
            </ToggleGroup>
          </div>
        </div>
      )}

      {/* Editor Container - scroll lives here so toolbar stays fixed */}
      <div className={cn("overflow-auto flex flex-col flex-1 min-h-0 w-full custom-scrollbar", editorClassName)}>
        <div
          ref={editorRef}
          contentEditable={!readOnly}
          onInput={readOnly ? undefined : handleInput}
          onPaste={readOnly ? undefined : handlePaste}
          className={cn(
            'wysiwyg-editor p-4 w-full max-w-[800px] min-h-full text-foreground focus:outline-none [&_*]:!text-foreground',
            readOnly && 'wysiwyg-read-only' // this class is used to hide the trash buttons
          )}
          style={{
            fontFamily: 'inherit',
            fontSize: '14px',
            lineHeight: '1.5',
          }}
          data-placeholder={placeholder}
        />
      </div>
    </div>
  )
} 